
import { useState, useEffect } from 'react';

export default function About() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('about');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const skills = [
    {
      category: 'Core Stack',
      items: ['ReactJS', 'TypeScript', 'Python', 'Golang', 'C/C++']
    },
    {
      category: 'Databases',
      items: ['PostgreSQL', 'MongoDB', 'Redis', 'Cassandra', 'ScyllaDB', 'Elasticsearch', 'Vector Databases']
    },
    {
      category: 'Web & APIs',
      items: ['React Query', 'FastAPI', 'REST', 'GraphQL', 'gRPC', 'WebSockets']
    },
    {
      category: 'AI & LLMs',
      items: ['LangChain', 'LangGraph', 'RAG Systems']
    },
    {
      category: 'DevOps & Cloud',
      items: ['Docker', 'Kubernetes', 'Terraform', 'Ansible', 'AWS', 'GCP', 'RabbitMQ', 'Kafka', 'Message Queues', 'Distributed Systems']
    }
  ];

  return (
    <section id="about" className="py-20 px-4 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(10)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-green-500/10 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'animate-slide-in-up' : 'opacity-0'}`}>
          <div className="inline-block bg-gray-900/80 border border-green-500/30 rounded-lg p-4 mb-8 backdrop-blur-md hover-glow">
            <div className="text-green-400 text-left">
              <span className="text-green-500 animate-pulse">$</span> cat about.md
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 gradient-text">About Me</h2>
          <div className="w-24 h-1 bg-gradient-to-r from-green-500 to-emerald-500 mx-auto animate-pulse"></div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left Column - Bio */}
          <div className={`space-y-6 transition-all duration-1000 ${isVisible ? 'animate-slide-in-left' : 'opacity-0'}`}>
            <div className="bg-gray-900/50 border border-green-500/30 rounded-lg p-6 backdrop-blur-sm hover-glow hover-scale">
              <h3 className="text-2xl font-bold text-green-400 mb-4 flex items-center">
                <i className="ri-code-s-slash-line mr-2 animate-pulse"></i>
                Core Expertise
              </h3>
              <div className="text-gray-300 space-y-4 leading-relaxed">
                <p className="hover:text-green-300 transition-colors duration-300">
                  I&apos;m a software engineer with a <span className="text-green-400 font-semibold gradient-text">tech-agnostic mindset</span>, 
                  moving comfortably between ReactJS frontends, Go and FastAPI backends, and systems-level C/C++ when performance really matters.
                </p>
                <p className="hover:text-green-300 transition-colors duration-300">
                  My recent focus is on building products with <span className="text-green-400 font-semibold gradient-text">ReactJS, Golang, FastAPI, and PostgreSQL</span>, 
                  often powered by <span className="text-green-400 font-semibold gradient-text">RAG systems using LangChain or LangGraph</span> for real-world AI features.
                </p>
                <p className="hover:text-green-300 transition-colors duration-300">
                  I care more about solving the problem than defending a specific stack, and I lean on solid foundations in 
                  <span className="text-green-400 font-semibold gradient-text"> algorithms, data structures, and system design</span> to choose the right tools.
                </p>
              </div>
            </div>

            <div className="bg-gray-900/50 border border-green-500/30 rounded-lg p-6 backdrop-blur-sm hover-glow hover-scale">
              <h3 className="text-2xl font-bold text-green-400 mb-4 flex items-center">
                <i className="ri-brain-line mr-2 animate-pulse"></i>
                Philosophy
              </h3>
              <div className="text-gray-300 leading-relaxed hover:text-green-300 transition-colors duration-300">
                <p>
                  I&apos;m <span className="text-green-400 font-semibold gradient-text">explicitly tech-agnostic</span>: I start from the problem, the users, 
                  and the constraints, then pick the stack that makes the most sense &mdash; whether that&apos;s React and Go, FastAPI and Python, 
                  or low-level C/C++.
                </p>
                <p className="mt-3">
                  I value clear architecture, good DX, and maintainable systems over hype, and I&apos;m comfortable working across 
                  frontend, backend, infra, and AI layers to ship end-to-end solutions.
                </p>
              </div>
            </div>
          </div>

          {/* Right Column - Skills */}
          <div className={`space-y-6 transition-all duration-1000 ${isVisible ? 'animate-slide-in-right' : 'opacity-0'}`} style={{ animationDelay: '0.3s' }}>
            <div className="bg-gray-900/50 border border-green-500/30 rounded-lg p-6 backdrop-blur-sm hover-glow hover-scale">
              <h3 className="text-2xl font-bold text-green-400 mb-6 flex items-center">
                <i className="ri-terminal-box-line mr-2 animate-pulse"></i>
                Technical Skills
              </h3>
              
              <div className="space-y-6">
                {skills.map((skillGroup, index) => (
                  <div 
                    key={index} 
                    className="border-l-2 border-green-500/30 pl-4 hover:border-green-500/60 transition-colors duration-300"
                    style={{ animationDelay: `${0.6 + index * 0.2}s` }}
                  >
                    <h4 className="text-lg font-semibold text-white mb-3 hover:text-green-400 transition-colors duration-300">{skillGroup.category}</h4>
                    <div className="flex flex-wrap gap-2">
                      {skillGroup.items.map((skill, skillIndex) => (
                        <span 
                          key={skillIndex}
                          className="bg-green-500/10 text-green-400 px-3 py-1 rounded-full text-sm border border-green-500/20 hover:bg-green-500/20 hover-scale cursor-pointer animate-float transition-all duration-300"
                          style={{ animationDelay: `${1 + skillIndex * 0.1}s` }}
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats with enhanced animations */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-900/50 border border-green-500/30 rounded-lg p-4 text-center backdrop-blur-sm hover-glow hover-scale animate-float">
                <div className="text-3xl font-bold text-green-400 gradient-text animate-pulse">5+</div>
                <div className="text-gray-300 text-sm">Years Experience</div>
              </div>
              <div className="bg-gray-900/50 border border-green-500/30 rounded-lg p-4 text-center backdrop-blur-sm hover-glow hover-scale animate-float" style={{ animationDelay: '0.5s' }}>
                <div className="text-3xl font-bold text-green-400 gradient-text animate-pulse">50+</div>
                <div className="text-gray-300 text-sm">Projects Completed</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
